<template>
    <div v-if="form.WorkOrderId" class="alert alert-info d-flex align-items-center mb-3 py-2">
        <i class="fas fa-lock me-2"></i>
        <span>This item is linked to <a href="javascript:void(0);" @click="$router.push('/invoices/edit/' + form.WorkOrderId)"><strong>Invoice {{ form.WorkOrder?.InvoiceNumber || '#' + form.WorkOrderId }}</strong></a> and cannot be edited.</span>
    </div>
    <fieldset :disabled="!!form.WorkOrderId">
        <div class="row g-3">
            <div class="col-lg-6">
                <div class="mb-2">
                    <i class="fas fa-briefcase me-2 text-muted"></i>
                    <strong>Job details</strong>
                </div>
                <div class="card h-100">
                    <div class="card-body">
                        <div class="mb-3">
                            <label class="d-block">
                                <span class="form-label">Technician <span class="text-danger">*</span></span>
                                <tech-ac 
                                    v-model="form.AssignedTo" 
                                    placeholder="Search for a technician..."
                                ></tech-ac>
                            </label>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="d-block">
                                        <span class="form-label">Client <span class="text-danger">*</span></span>
                                        <client-ac
                                            v-model="form.CustomerNumber"
                                            inputclass="form-control form-control-sm"
                                            placeholder="Search for a client..."
                                        ></client-ac>
                                    </label>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="d-block">
                                        <span class="form-label">Request/Job <span class="text-danger">*</span></span>
                                        <job-ac
                                            v-model="form.RequestId"
                                            :client="form.CustomerNumber"
                                            placeholder="Search for a request..."
                                        ></job-ac>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Work Date <span class="text-danger">*</span></span>
                                    <div class="input-group input-group-sm">
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            @click="adjustWorkDate(-1)"
                                            title="Previous day"
                                        >
                                            <i class="fa fa-chevron-left"></i>
                                        </button>
                                        <input type="datetime-local" 
                                            class="form-control form-control-sm" 
                                            v-model="form.WorkCompleteDate" 
                                            placeholder="Work Date"
                                        >
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            @click="adjustWorkDate(1)"
                                            title="Next day"
                                        >
                                            <i class="fa fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Location</span>
                                    <select 
                                        class="form-select form-select-sm" 
                                        v-model="form.TaskLocation"
                                    >
                                        <option :value="undefined">Select Location...</option>
                                        <option 
                                            v-for="loc in locationOptions" 
                                            :key="loc.value" 
                                            :value="loc.value"
                                        >
                                            {{ loc.label }}
                                        </option>
                                    </select>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="mb-2 d-flex justify-content-between align-items-center">
                    <span>
                        <i class="fas fa-file-invoice-dollar me-2 text-muted"></i>
                        <strong>Billing info</strong>
                    </span>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="calculateBilling()"
                        title="Recalculate billing from rates"
                    >
                        <i class="fa fa-calculator me-1"></i>Recalculate
                    </button>
                </div>
                <div class="card h-100">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Hours Worked <span class="text-danger">*</span></span>
                                    <input
                                        class="form-control form-control-sm"
                                        type="number"
                                        step="0.25"
                                        v-model="form.ActualTime"
                                        placeholder="Hours"
                                    />
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Invoice Time (hrs)</span>
                                    <input
                                        class="form-control form-control-sm"
                                        type="number"
                                        step="0.25"
                                        v-model="form.InvoiceTime"
                                        placeholder="0.00"
                                    />
                                </label>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Invoice Amount ($)</span>
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text">$</span>
                                        <input
                                            class="form-control form-control-sm"
                                            type="number"
                                            step="0.01"
                                            v-model="form.InvoiceAmount"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Should Bill ($)</span>
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text">$</span>
                                        <input
                                            class="form-control form-control-sm"
                                            type="number"
                                            step="0.01"
                                            v-model="form.ShouldBill"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </label>
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="d-block">
                                <span>Status</span>
                                <select 
                                    class="form-select form-select-sm" 
                                    v-model="form.Status"
                                >
                                    <option :value="undefined">Select Status...</option>
                                    <option 
                                        v-for="status in statusOptions" 
                                        :key="status.value" 
                                        :value="status.value"
                                    >
                                        {{ status.label }}
                                    </option>
                                </select>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-5">
                <div class="mb-2">
                    <i class="fas fa-clipboard-list me-2 text-muted"></i>
                    <strong>Notes</strong>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div class="mb-3" v-if="form.WorkAssignment">
                            <label class="d-block">
                                <span class="text-muted">Work Assignment (original task)</span>
                                <textarea class="form-control form-control-sm" :value="form.WorkAssignment" readonly
                                    rows="3"></textarea>
                            </label>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Work Description</span>
                                    <textarea class="form-control form-control-sm" placeholder="Describe the work performed" v-model="form.ActualWork"
                                        rows="4"></textarea>
                                </label>
                            </div>
                            <div class="col-md-6">
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                    <span>Invoice Text</span>
                                    <button
                                        type="button"
                                        class="btn btn-outline-secondary btn-sm py-0 px-2"
                                        @click="autoGenerateInvoiceText"
                                        :disabled="aiState.IsLoading || !form.ActualWork"
                                        :title="form.ActualWork ? 'Generate invoice text from Work Description via AI' : 'Enter Work Description first'"
                                    >
                                        <i class="fas fa-wand-magic-sparkles me-1"></i>
                                        {{ aiState.IsLoading ? 'Generating...' : 'Auto' }}
                                    </button>
                                </div>
                                <textarea class="form-control form-control-sm" placeholder="Text to appear on invoice..." v-model="form.InvoiceText"
                                    rows="4"></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </fieldset>
</template>

<script lang="ts" setup>
import { computed, watch, ref } from "vue";
import TechAc from "@/components/Techs/TechAc.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import { storeToRefs } from "pinia";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import OptionsService from "@/services/OptionsService";
import ApiService from "@/services/ApiService";
import Api from "@/services/Api";
import Swal from "sweetalert2";
import Toast from "@/services/Toast";
import { useApiRequest } from "@/composables";

const taskStatusStore = useTaskStatusStore();
const { list: taskStatusList } = storeToRefs(taskStatusStore);

// Convert store list to options array for select dropdown
const statusOptions = computed(() => 
    Object.values(taskStatusList.value).map(status => ({
        value: status.Id,
        label: status.Status
    }))
);

const locationOptions = [
    { value: 1, label: "ON-SITE" },
    { value: 2, label: "GTA" },
    { value: 3, label: "WEB" },
    { value: 4, label: "PHONE/EMAIL" },
    { value: 5, label: "IN HOUSE" },
    { value: 6, label: "OVERSIGHT" }
];

const model = defineModel<any>();

const form = computed({
    get: () => {
        if (model.value && model.value.Type !== 'work') {
            model.value.Type = 'work';
        }
        return model.value || {};
    },
    set: (value) => {
        if (value) {
            value.Type = 'work';
        }
        model.value = value;
    }
});

const clientBillingRates = ref<Record<string, number>>({});
const techBillingRates = ref<Record<string, number>>({});
const jobDepartment = ref<string | null>(null);

// Guard against multiple concurrent rate prompts
const isPromptingRate = ref(false);

// AI invoice-text generation request state (uses useApiRequest per project rules)
const { state: aiState, execute: executeAiChat } = useApiRequest<any>('workForm:aiInvoice');

const autoGenerateInvoiceText = async () => {
    const actualWork = (form.value.ActualWork || '').trim();
    if (!actualWork) {
        Toast.error('Enter Work Description first so the AI has something to summarize');
        return;
    }

    const messages = [
        {
            Role: 'system',
            Content:
                'You are a billing assistant for an IT services company. ' +
                'Given a technician\'s description of work performed, write concise, ' +
                'professional invoice text suitable for a customer invoice. ' +
                'Use plain prose (no headings, no bullet points, no markdown). ' +
                'Keep it under 4 sentences. Do not include pricing, dates, time worked, hours, or technician names. ' +
                'The work description may contain clock start/stop timestamps — ignore them entirely.'
        },
        {
            Role: 'user',
            Content: `Write invoice text based on this work description:\n\n${actualWork}`
        }
    ];

    await executeAiChat(() => ApiService.aiChat(messages));

    if (aiState.value.IsError) {
        Toast.error(aiState.value.Message || 'AI invoice text generation failed');
        return;
    }

    const result = aiState.value.Data;
    const text = (result?.Content || '').trim();
    if (!text) {
        Toast.error('AI returned no invoice text');
        return;
    }

    form.value.InvoiceText = text;
    Toast.success('Invoice text generated from Work Description');
};

// Round hours up to the next quarter hour (0.25 increment).
// 1.01 → 1.25, 1.14 → 1.25, 1.25 → 1.25, 1.26 → 1.50, 1.00 → 1.00
const roundUpToQuarterHour = (hours: number): number => {
    return Math.ceil(hours * 4) / 4;
};

// Adjust work date by days
const adjustWorkDate = (days: number) => {
    if (!form.value.WorkCompleteDate) {
        // If no date set, use today
        const today = new Date();
        form.value.WorkCompleteDate = today.toISOString().slice(0, 16);
    }
    
    const currentDate = new Date(form.value.WorkCompleteDate);
    currentDate.setDate(currentDate.getDate() + days);
    form.value.WorkCompleteDate = currentDate.toISOString().slice(0, 16);
};

const loadClientBillingRates = async (customerNumber: string) => {
    if (!customerNumber) {
        clientBillingRates.value = {};
        return;
    }
    
    try {
        const optionKey = 'billing_rates_contact';
        const savedRates = await OptionsService.get(optionKey, 'Contact', customerNumber);
        
        if (savedRates) {
            clientBillingRates.value = JSON.parse(savedRates);
        } else {
            clientBillingRates.value = {};
        }
    } catch (error) {
        console.error('Error loading client billing rates:', error);
        clientBillingRates.value = {};
    }
};

const loadTechBillingRates = async (techId: string) => {
    if (!techId) {
        techBillingRates.value = {};
        return;
    }
    
    try {
        const optionKey = 'billing_rates_technician';
        const savedRates = await OptionsService.get(optionKey, 'Technician', techId);
        
        if (savedRates) {
            techBillingRates.value = JSON.parse(savedRates);
        } else {
            techBillingRates.value = {};
        }
    } catch (error) {
        console.error('Error loading tech billing rates:', error);
        techBillingRates.value = {};
    }
};

const loadJobDepartment = async (requestId: string) => {
    if (!requestId) {
        jobDepartment.value = null;
        return;
    }
    
    try {
        const job = await Api.loadJob(requestId, true);
        jobDepartment.value = job?.DepartmentCategory || null;
    } catch (error) {
        console.error('Error loading job department:', error);
        jobDepartment.value = null;
    }
};

const calculateBilling = async (skipPrompt: boolean = false) => {
    if (!form.value.ActualTime) {
        return;
    }

    const hours = Number(form.value.ActualTime) || 0;
    if (hours <= 0) return;

    // Always update Invoice Time, rounded up to the next quarter hour
    const invoiceTime = roundUpToQuarterHour(hours);
    form.value.InvoiceTime = invoiceTime;

    let rate: number | null = null;

    // Client rate takes precedence (requires a department to look up)
    if (jobDepartment.value && clientBillingRates.value[jobDepartment.value]) {
        rate = clientBillingRates.value[jobDepartment.value];
    }
    // Fall back to technician rate
    else if (jobDepartment.value && techBillingRates.value[jobDepartment.value]) {
        rate = techBillingRates.value[jobDepartment.value];
    }

    // If no billable rate was found, prompt the user for one (unless skipPrompt is set)
    if ((rate === null || rate <= 0) && !skipPrompt) {
        if (isPromptingRate.value) return;
        isPromptingRate.value = true;

        try {
            const deptLabel = jobDepartment.value
                ? `the "${jobDepartment.value}" department`
                : 'this job';
            const result = await Swal.fire({
                title: 'Billable Rate Required',
                text: `No billing rate was found for ${deptLabel}. Please enter a billable rate ($/hr) to calculate the invoice amount.`,
                input: 'number',
                inputLabel: 'Billable Rate ($/hr)',
                inputPlaceholder: 'Enter rate per hour',
                showCancelButton: true,
                confirmButtonText: 'Calculate',
                cancelButtonText: 'Skip',
                inputValidator: (value: string) => {
                    if (!value || Number(value) <= 0) {
                        return 'Please enter a valid rate greater than 0';
                    }
                    return undefined;
                }
            });

            if (result.isConfirmed && result.value) {
                rate = Number(result.value);
            } else {
                // User skipped — leave amounts as-is
                return;
            }
        } catch (error) {
            console.error('Error prompting for billable rate:', error);
            return;
        } finally {
            isPromptingRate.value = false;
        }
    }

    if (rate !== null && rate > 0) {
        const total = invoiceTime * rate;
        form.value.ShouldBill = total;
        form.value.InvoiceAmount = total;
    }
};

// Watch for changes to load rates silently (no popup — only the Recalculate button prompts)
watch(() => form.value.CustomerNumber, async (newVal) => {
    if (newVal) {
        await loadClientBillingRates(newVal.toString());
        await calculateBilling(true);
    }
});

watch(() => form.value.AssignedTo, async (newVal) => {
    if (newVal) {
        await loadTechBillingRates(newVal.toString());
        await calculateBilling(true);
    }
});

watch(() => form.value.RequestId, async (newVal) => {
    if (newVal) {
        await loadJobDepartment(newVal.toString());
        await calculateBilling(true);
    }
});

watch(() => form.value.ActualTime, () => {
    calculateBilling(true);
});
</script>
